// Kinio 事件（SPEC #platform App 面的 Thing API、#eng-10 分頁）。
//
//   GET {api}/v1/homes/{home_id}/things/{device}/events?from=&to=&limit=&cursor=&lens=
//     → {events: […], next_cursor?}，新的在前；limit 預設 100、上限 500；from / to 是 RFC 3339、[from, to)；
//       沒有下一頁不帶 next_cursor。from / to 壞 → 400 BAD_RANGE、cursor 壞 → 400 BAD_CURSOR。
//     多鏡頭（plan SPEC #multi-lens-recording）：`lens` 沒帶 = 只回 lens 0（舊呼叫端不變）、`lens=all` 全部；每筆帶 `lens`（0 可省略）。
//
// 沒有推送（P1 用進頁重拉 / 手動重新整理 / 輪詢）。縮圖欄位還沒有：設備事件的
// `payload.snapshot_url` 之後才定，目前 UI 用型別圖示。

import { kinioFetch } from './auth';
import { planeBase } from './config';

export const EVENTS_DEFAULT_LIMIT = 100;
export const EVENTS_MAX_LIMIT = 500;

export interface KinioEvent {
  id: string;
  /** Thing 的定址鍵（SPEC #terms）。 */
  device_ref: string;
  thing_id?: string;
  /** `motion` | `person` | `thing.online` | `thing.offline` | `ota.*` | … */
  type: string;
  source?: 'device' | 'gateway' | string;
  payload?: Record<string, unknown>;
  /** RFC3339 */
  ts: string;
  /** unix 秒（設備自己的時鐘；可能缺） */
  device_ts?: number;
  transport?: string;
  /** 多鏡頭設備的鏡頭（0 = 主鏡頭，可省略；`lensOf(ev)` 取值）。 */
  lens?: number;
  [k: string]: unknown;
}

export interface ListEventsOptions {
  limit?: number;
  /** RFC 3339（含）；`Date` 會轉成 ISO 字串。 */
  from?: string | Date;
  /** RFC 3339（不含）。 */
  to?: string | Date;
  /** 上一頁的 `next_cursor`。 */
  cursor?: string;
  /** 多鏡頭：沒帶 = 只回主鏡頭（lens 0）的事件；`'all'` = 全部鏡頭；數字 = 那一顆。 */
  lens?: number | 'all';
  signal?: AbortSignal;
}

export interface EventsPage {
  events: KinioEvent[];
  /** 有下一頁才有。 */
  next_cursor?: string;
}

const rfc3339 = (v: string | Date) => (v instanceof Date ? v.toISOString() : v);

/** GET …/things/{device}/events → `{events, next_cursor?}`（新到舊）。 */
export async function listEvents(apiEndpoint: string, homeId: string, device: string, opts: ListEventsOptions = {}): Promise<EventsPage> {
  const q = new URLSearchParams({ limit: String(Math.min(Math.max(opts.limit ?? EVENTS_DEFAULT_LIMIT, 1), EVENTS_MAX_LIMIT)) });
  if (opts.from !== undefined) q.set('from', rfc3339(opts.from));
  if (opts.to !== undefined) q.set('to', rfc3339(opts.to));
  if (opts.cursor) q.set('cursor', opts.cursor);
  if (opts.lens !== undefined) q.set('lens', String(opts.lens));
  const url = `${planeBase(apiEndpoint)}/v1/homes/${encodeURIComponent(homeId)}/things/${encodeURIComponent(device)}/events?${q}`;
  const res = await kinioFetch<EventsPage>(url, { signal: opts.signal });
  return { events: res?.events ?? [], ...(res?.next_cursor ? { next_cursor: res.next_cursor } : {}) };
}

/** 多台合併（各自抓第一頁、依時間新到舊合併）。任一台失敗不影響其他台。 */
export async function listEventsForThings(
  apiEndpoint: string,
  homeId: string,
  devices: string[],
  opts: ListEventsOptions = {},
): Promise<{ events: KinioEvent[]; errors: Record<string, unknown> }> {
  const errors: Record<string, unknown> = {};
  const lists = await Promise.all(
    devices.map(async (device) => {
      try {
        return (await listEvents(apiEndpoint, homeId, device, opts)).events;
      } catch (e) {
        errors[device] = e;
        return [] as KinioEvent[];
      }
    }),
  );
  const events = lists.flat().sort((a, b) => eventTsMs(b) - eventTsMs(a));
  return { events, errors };
}

// ---- 型別分類 / 顯示 ----

export type EventCategory = 'motion' | 'person' | 'sound' | 'doorbell' | 'online' | 'offline' | 'ota' | 'recording' | 'other';

/** `thing.online` → online；`ota.progress` → ota；未知 → other。 */
export function eventCategory(type: string | undefined | null): EventCategory {
  const t = (type ?? '').trim().toLowerCase();
  if (!t) return 'other';
  if (t === 'motion' || t.endsWith('.motion')) return 'motion';
  if (t === 'person' || t.endsWith('.person') || t === 'human') return 'person';
  if (t === 'sound' || t === 'audio' || t.endsWith('.sound')) return 'sound';
  if (t === 'doorbell' || t.endsWith('.doorbell') || t === 'button') return 'doorbell';
  if (t === 'thing.online' || t === 'online') return 'online';
  if (t === 'thing.offline' || t === 'offline') return 'offline';
  if (t.startsWith('ota.') || t === 'ota') return 'ota';
  if (t.startsWith('recording') || t === 'clip' || t.startsWith('rec.')) return 'recording';
  return 'other';
}

/** 事件圖示（emoji；UI 以 aria-hidden 裝飾呈現，文字說明另出）。 */
export function eventIcon(category: EventCategory): string {
  switch (category) {
    case 'motion':
      return '🏃';
    case 'person':
      return '👤';
    case 'sound':
      return '🔊';
    case 'doorbell':
      return '🔔';
    case 'online':
      return '🟢';
    case 'offline':
      return '⚪';
    case 'ota':
      return '⬆️';
    case 'recording':
      return '⏺';
    default:
      return '•';
  }
}

/** 事件時間（epoch ms）。`ts` 解析不出就退回 `device_ts`（秒）。 */
export function eventTsMs(ev: Pick<KinioEvent, 'ts' | 'device_ts'>): number {
  const ms = Date.parse(ev.ts ?? '');
  if (Number.isFinite(ms)) return ms;
  if (typeof ev.device_ts === 'number' && ev.device_ts > 0) return ev.device_ts * 1000;
  return 0;
}

/** µs（錄影 API 用同一個時間軸）。 */
export function eventTsUs(ev: Pick<KinioEvent, 'ts' | 'device_ts'>): number {
  return eventTsMs(ev) * 1000;
}

/** 相對時間（i18n 由呼叫端包；這裡回結構，避免在 lib 綁字串）。 */
export function relativeParts(tsMs: number, now = Date.now()): { unit: 'now' | 'minute' | 'hour' | 'day'; value: number } {
  const diff = Math.max(0, now - tsMs);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return { unit: 'now', value: sec };
  const min = Math.floor(sec / 60);
  if (min < 60) return { unit: 'minute', value: min };
  const hour = Math.floor(min / 60);
  if (hour < 24) return { unit: 'hour', value: hour };
  return { unit: 'day', value: Math.floor(hour / 24) };
}
